import React, {useEffect, useRef, useState} from 'react';
import Button from "react-bootstrap/Button";
import Form from 'react-bootstrap/Form';
import { useForm } from "react-hook-form";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import {sanitizePhoneNumber, createElementFromHTML, validateEmail} from "../../utils/utilsHelpers";
import Modal from "react-bootstrap/Modal";
import ConditionalWrapper from "../../utils/ConditionalWrapper";
import { objectMap } from "../../../javascripts/utils";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
// import * as Sentry from "@sentry/react";

// https://github.com/react-hook-form/react-hook-form/tree/master/examples

const EMAIL_PATTERN = /\S+@\S+\.\S{2,}/;

function LeadForm(props) {

    let {
        token,
        gdpr,
        showPhone,
        showCountry,
        emailOnly,
        buttonOnly,
        cta,
        currentUser,
        entryPage,
        leadCode,
        countries,
        gaReferrer,
        gaClientId,
        hiddenTags,
        additionalLeadData,
        vendor,
        userSignedIn,
        modalHeader,
        modalSubheader='Share a few more details so we can provide specific suggestions for you'
    } = props;

    // Also in props: onLeadCreate

    const { register, handleSubmit, clearErrors, setError, setValue, formState: { errors, isValid, isSubmitting }} = useForm({ mode: 'onBlur'});

    // const [userCountry, setUserCountry] = useState('US');
    const [isPhoneValid, setIsPhoneValid] = useState(false);
    const [phone, setPhone] = useState(null);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [submitCta, setSubmitCta] = useState(cta);
    const isPhoneValidRef = useRef(isPhoneValid);
    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
        if(!emailOnly) {
            register('user.phone', {
                pattern: { value: /^([^a-zA-z]*)$/g, message: 'This phone number is invalid' },
                validate: () => isPhoneValidRef.current || 'This phone number is invalid' });
        }
    }, [register]);

    useEffect(() => {
        setValue('user.phone', phone)
        clearErrors('user.phone');
    }, [phone]);

    useEffect(() => {
        isPhoneValidRef.current = isPhoneValid;
    }, [isPhoneValid]);

    useEffect(() => {
        objectMap(additionalLeadData, (value, key) => {
            register(key);
            setValue(key, value);
        });
    }, [additionalLeadData]);

    // console.log(formState.errors); // debugging: remove : { errors } from useForm above and replace all 'errors' below with 'formState.errors'

    const renderModalHeader = () => {
        if(buttonOnly) {
            return (
                <Modal.Header className={'border-bottom-0 pt-4 pb-2 justify-content-center text-center'}>
                    <Modal.Title className={`fs-4 ${modalSubheader && 'mb-0'}`}>
                        {modalHeader}
                    </Modal.Title>
                </Modal.Header>
            )
        }
    }

    const renderModalSubHeader = () => {
        if(modalSubheader) {
            return (
                <div className={'mb-4 secondary-links fs-6 mx-auto w-75'}>
                    {modalSubheader}
                </div>
            )
        }
    }

    const renderEmailField = () => {
        return (
            <Form.Group className="mb-3 text-start">
                <Form.Control
                    type="email"
                    id="user_email"
                    placeholder="Company email"
                    onKeyUp={(e) => validateEmail(e.target.value, 'user.email', isEmailValid, setIsEmailValid, setEmailError, setError, clearErrors)}
                    {...register("user.email", {
                        required: 'This field is required',
                        pattern: {
                            value: EMAIL_PATTERN,
                            message: "Please enter a valid email address"
                        },
                        validate: () => isEmailValid || emailError
                    })} />
                {errors.user?.email && <Form.Text className="text-danger">{errors.user.email.message}</Form.Text>}
            </Form.Group>
        )
    }

    const renderPhoneField = () => {
        if(showPhone) {
            return (
                <Form.Group className="mb-3 text-start">
                    <IntlTelInput
                        inputProps={ { className: 'form-control text-secondary' }}
                        initialValue={'+1'}
                        onChangeNumber={setPhone}
                        onChangeValidity={setIsPhoneValid}
                        initOptions={{
                            containerClassName: 'd-block',
                            nationalMode: false
                        }}
                    />
                    {errors.user?.phone && <Form.Text className="text-danger">{errors.user.phone.message}</Form.Text>}
                </Form.Group>
            )
        } else {
            return <input type='hidden' value={userSignedIn && currentUser.phone} className={'user-phone'} {...register('user.phone')} />
        }
    }

    const renderCountryField = () => {
        return (
            <Form.Group className="mb-3 text-start">
                <Form.Select aria-label="Select country" {...register('user.country')} >
                    <option disabled={'disabled'} selected={true}>Select your country</option>
                    {countries.map((c, i) => {
                        return (
                            <option key={i} value={c[1]}>{c[0]}</option>
                        )
                    })}
                </Form.Select>
            </Form.Group>
        )
    }

    const renderGdpr = () => {
        return (
            <Form.Check type='checkbox' id='gdpr_checkbox' className={'form-check-primary secondary-links'}>
                <Form.Check.Input type='checkbox' {...register("gdpr_checkbox", { required: 'This field is required' })} />
                <Form.Check.Label className={'content-styling text-dark font-12 text-start'}>{createElementFromHTML(gdpr)}
                    {errors.gdpr_checkbox && <Form.Text className="text-danger fw-normal">{errors.gdpr_checkbox.message}</Form.Text>}
                </Form.Check.Label>
            </Form.Check>
        )
    }

    const renderVendorDisclaimer = () => {
        if(vendor?.disclaimer?.length) {
            return (
                <Form.Check type='checkbox' id='lead_vendor_consent' className={'form-check-primary secondary-links'}>
                    {vendor?.opt_in && <input type='hidden' id='lead_vendor_id' key='lead_vendor_id' value={vendor.id} {...register('lead.vendor_id')}  />}
                    {vendor?.opt_in && <Form.Check.Input type='checkbox' {...register('lead.vendor_consent')} />}
                    <Form.Check.Label className={'content-styling text-dark font-12 text-start'}>{createElementFromHTML(vendor?.disclaimer)}</Form.Check.Label>
                </Form.Check>
            )
        }
    }

    const renderFormFields = () => {
        if(emailOnly) {
            return (
                <>
                    {renderEmailField()}
                    {gdpr?.length && renderGdpr()}
                    {vendor?.length && renderVendorDisclaimer()}
                </>
            )
        }

        return (
            <>
                <Form.Group className="mb-3 text-start">
                    <Form.Control
                        id='user_first_name'
                        placeholder='First name'
                        // value={userSignedIn && currentUser.first_name}
                        {...register("user.first_name", { pattern: { value: /^([^0-9]*)$/g, message: 'First name cannot contain numbers' } })} />
                    {errors.user?.first_name && <Form.Text className="text-danger">{errors.user.first_name.message}</Form.Text>}
                </Form.Group>

                <Form.Group className="mb-3 text-start">
                    <Form.Control
                        id='user_last_name'
                        placeholder='Last name'
                        // value={userSignedIn && currentUser.last_name}
                        {...register("user.last_name", { pattern: { value: /^([^0-9]*)$/g, message: 'Last name cannot contain numbers' } })} />
                    {errors.user?.last_name && <Form.Text className="text-danger">{errors.user.last_name.message}</Form.Text>}
                </Form.Group>

                {!emailOnly && renderEmailField()}

                {renderPhoneField()}

                {showCountry && renderCountryField()}

                {gdpr?.length && renderGdpr()}

                {vendor?.length && renderVendorDisclaimer()}
            </>
        )
    }

    const renderHiddenFields = () => {
        let hiddenInputs = hiddenTags && objectMap(hiddenTags, (value, name) => {
            return <input type='hidden' id={name} key={name} value={value} {...register(name)} />
        });

        let leadCodeField = leadCode?.length && <input type='hidden' id='lead_code' key='lead_code' value={leadCode} {...register('lead.lead_code')} />;
        return (
            <>
                <input type='hidden' id='authenticity_token' key='authenticity_token' value={token} {...register('authenticity_token')} />
                {/*<input type='hidden' id='lead_ga_client_id' key='lead_ga_client_id' value={gaClientId} {...register('lead.ga_client_id')} />*/}
                <input type='hidden' id='lead_entry_page' key='lead_entry_page' value={entryPage} {...register('lead.entry_page')} />
                { hiddenInputs }
                { leadCodeField }
            </>
        )
    }

    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const onLeadSubmit = async(data) => {
        if(!emailOnly) data.user.phone = sanitizePhoneNumber(data.user.phone);
        if(!isEmailValid) return;
        let captchaToken = "";
        try {
            captchaToken = await executeRecaptcha('send_report');
        } catch (error) {
            console.error('Error during recaptcha verification:', error);
        }
        setSubmitCta('Submitting...');
        const payload = {...data, "g-recaptcha-response-data": { "send_report": captchaToken }};
        await fetch('/users', {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload)
        }).then(function(response) {
            try {
                return response.json();
            } catch (err) {
                // Sentry.captureException(`Error on lead submit. Data sent: ${data}. Response: ${response}. Error: ${err}`)
            }
        }).then(function(jsonResponse) {
            if(jsonResponse.success) {
                window.dataLayer.push({'event': 'lead_submitted', event_action: 'lead_submitted',
                    event_category: 'download_for_lead_page',
                    event_label: gaReferrer });
                window._vis_opt_queue = window._vis_opt_queue || [];
                window._vis_opt_queue.push(function() {_vis_opt_goal_conversion(200);});
                const updatedUser = {...jsonResponse.user, ...data.user}; // include user fields from jsonResponse
                props.onLeadCreate({...jsonResponse, ...data, user: updatedUser});
            } else if(jsonResponse.errors?.email) {
                setError('user.email', { type: 'manual', message: jsonResponse.errors.email });
                setEmailError(jsonResponse.errors.email);
                setIsEmailValid(false);
            }
        });
        setSubmitCta(cta);
    }

    return (
        <>
            {renderModalHeader()}
            <ConditionalWrapper condition={buttonOnly}
                                wrapper={children => <Modal.Body className={`text-secondary text-center  px-1 px-sm-4 mx-1 ${modalSubheader && 'pt-0'}`}>{children}</Modal.Body>}
            >
                {renderModalSubHeader()}
                <Form
                    noValidate={true}
                    acceptCharset='UTF-8'
                    onSubmit={handleSubmit(onLeadSubmit)}
                >
                    {renderHiddenFields()}
                    {renderFormFields()}
                    <Button variant="primary" type="submit" className={'btn-block btn-hover-cursor mt-4'} disabled={isSubmitting}>
                        {submitCta}
                    </Button>
                </Form>
            </ConditionalWrapper>
        </>
    )
}

LeadForm.displayName = 'LeadForm';

export default LeadForm;